import "@app/App.css";
import { Workspace } from "../../dto/workspaces";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { Form } from "@comps/ui/form";
import { Button } from "@comps/ui/button";
import { del } from "@utils/utils";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { t } from "i18next";
import { TW_Button, TW_CompozzSpinnerContainer, TW_WorkspaceMenuItem, getWsButtonStyle, useHover } from "@utils/twClasses";
import { Trash } from "iconsax-react";
import { BASE_URL } from "@utils/constants";
import CompozzSpinner from "@comps/ui/CompozzSpinner";
import { useModelStore } from "src/store";

interface WorkspaceMenuDeleteItemProps {
  workspace: Workspace;
  onWorkpaceDelete: (workspace: Workspace) => void;
  closeMenu: () => void;
}

function WorkspaceMenuDeleteItem({ workspace, onWorkpaceDelete, closeMenu }: WorkspaceMenuDeleteItemProps) {

  const { t } = useTranslation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const workspaceDeleted = (workspace: Workspace) => {
    onWorkpaceDelete(workspace);
    setIsDialogOpen(false);
    closeMenu();
  }

  function cancel(event: React.MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    event.stopPropagation();
    setIsDialogOpen(false);
    closeMenu();
  }

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setIsDialogOpen(true);
  }

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DialogTrigger className={TW_WorkspaceMenuItem} onClick={handleClick}><Trash className="WorkspaceMenuItemIcon" />{t("workspace.button.delete.text")}</DialogTrigger>
      <DialogContent onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle>{t("workspace.dialog.delete.title")}</DialogTitle>
          <DialogDescription>{t("workspace.dialog.delete.description")}</DialogDescription>
        </DialogHeader>
        <WorkspaceDeleteForm initialWorkspace={workspace} onWorkpaceDelete={workspaceDeleted} onCancel={cancel} />
      </DialogContent>
    </Dialog>
  )
}

interface WorkspaceDeleteFormProps {
  initialWorkspace: Workspace;
  onWorkpaceDelete: (workspace: Workspace) => void;
  onCancel: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

function WorkspaceDeleteForm({ initialWorkspace, onWorkpaceDelete, onCancel }: WorkspaceDeleteFormProps) {
  const [loading, setLoading] = useState(false);
  const formSchema = z.object({})
  const wsColor = useModelStore(state => state.wsColor);
  const wsDarkColor = useModelStore(state => state.wsDarkColor);
  const cancelButtonStyle = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));
  const okButtonStyle = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));

  const form = useForm<z.infer<typeof formSchema>>({
  })

  function onSubmit(values: z.infer<typeof formSchema>) {
    setLoading(true);
    del(`${BASE_URL}/model/workspaces/${initialWorkspace._key}`, null).then(response => {
      onWorkpaceDelete(initialWorkspace);
    }).finally(() => {
      setLoading(false);
    })
  }

  function handleSubmit(event: React.MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    event.stopPropagation();
    form.handleSubmit(onSubmit)();
  }

  if (loading) {
    return <div className={TW_CompozzSpinnerContainer}>
      <CompozzSpinner />
    </div>
  }

  return (
    <Form {...form}>
      <form className="space-y-8">
        <div className="ButtonList">
          <Button className={TW_Button} {...cancelButtonStyle} onClick={onCancel}>{t("general.button.cancel.text")}</Button>
          <Button className={TW_Button} {...okButtonStyle} onClick={handleSubmit}>{t("general.button.confirm.text")}</Button>
        </div>
      </form>
    </Form>
  )
}

export default WorkspaceMenuDeleteItem;
